"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

import Icon from "@/components/ui/AppIcon";

export default function HeroSection() {
  const heroContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const items = heroContentRef.current?.querySelectorAll(".hero-item");
    items?.forEach((el, i) => {
      const htmlEl = el as HTMLElement;
      htmlEl.style.opacity = "0";
      htmlEl.style.transform = "translateY(32px)";
      htmlEl.style.filter = "blur(5px)";
      htmlEl.style.transition =
      "opacity 0.85s cubic-bezier(0.22,1,0.36,1), transform 0.85s cubic-bezier(0.22,1,0.36,1), filter 0.75s ease";
      htmlEl.style.transitionDelay = `${1.2 + i * 0.18}s`;
      setTimeout(() => {
        htmlEl.style.opacity = "1";
        htmlEl.style.transform = "translateY(0)";
        htmlEl.style.filter = "blur(0)";
      }, 100);
    });
  }, []);

  return (
    <section className="hero-section" aria-label="Foundation hero">
      {/* Background Image */}
      <div className="absolute inset-0 z-0 overflow-hidden bg-forest-dark">
        <img
          src="https://img.rocket.new/generatedImages/rocket_gen_img_1d3c26916-1771137569020.png"
          alt="Healthcare workers in Liberia serving communities"
          className="hero-bg-img object-cover animate-hero-cinematic"
          style={{ animationFillMode: "forwards" }} />
        
        {/* Gradient overlay */}
        <div className="hero-overlay" />
        {/* Bottom fade */}
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0F2A1F] to-transparent" />
      </div>

      {/* Floating top-right badge */}
      <div
        className="absolute top-28 right-6 md:right-12 z-20 animate-float"
        style={{ animationDelay: "2s" }}>
        
        <div
          className="hero-glass-card px-4 py-3 flex items-center gap-3"
          style={{ borderRadius: "12px" }}>
          
          <span className="w-2 h-2 rounded-full bg-gold animate-pulse flex-shrink-0" />
          <span className="text-xs font-body font-semibold tracking-wider uppercase text-white/90">SERVING LIBERIA SINCE 2026

          </span>
        </div>
      </div>

      {/* Vertical grid lines (decorative) */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none flex justify-between px-6 md:px-16 opacity-10">
        
        {[0, 1, 2, 3].map((i) =>
        <div key={i} className="w-px h-full bg-white/30" />
        )}
      </div>

      {/* Main Content */}
      <div
        ref={heroContentRef}
        className="relative z-10 w-full max-w-container mx-auto px-6 md:px-12 pb-16 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
        
        {/* Left column — headline */}
        <div className="md:col-span-7 flex flex-col gap-6">
          {/* Eyebrow */}
          <div className="hero-item">
            <span className="section-eyebrow text-gold-light">
              Dr. Walter T. Gwenigale Foundation
            </span>
          </div>

          {/* Main headline */}
          <h1 className="hero-item font-heading text-white leading-[0.9] tracking-tight">
            <span
              className="block"
              style={{
                fontSize: "clamp(2.8rem, 7vw, 5.5rem)",
                fontWeight: 700
              }}>
              
              Healing a Nation.
            </span>
            <span
              className="block italic font-light"
              style={{
                fontSize: "clamp(2.8rem, 7vw, 5.5rem)",
                color: "rgba(226, 201, 126, 0.9)"
              }}>
              
              Honoring a Legacy.
            </span>
          </h1>

          {/* Subheadline */}
          <p
            className="hero-item text-white/70 font-body font-light leading-relaxed max-w-lg"
            style={{ fontSize: "clamp(1rem, 2vw, 1.125rem)" }}>Carrying forward the vision of Liberia's most dedicated physician — equitable healthcare and education for every Liberian, in every corner of the country.




          </p>

          {/* CTAs */}
          <div className="hero-item flex flex-wrap gap-4 mt-2">
            <a href="#donate" className="btn-primary">
              <Icon name="HeartIcon" size={18} variant="solid" />
              Support Our Mission
            </a>
            <a href="#legacy" className="btn-outline-light">
              <Icon name="BookOpenIcon" size={18} />
              Learn His Story
            </a>
          </div>

          {/* Memorial dates */}
          <div className="hero-item flex items-center gap-3 mt-2">
            <div className="w-8 h-px bg-gold/60" />
            <span className="text-white/50 text-xs font-body tracking-widest uppercase">
              February 4, 1935 — April 22, 2022
            </span>
          </div>
        </div>

        {/* Right column — glass info card */}
        <div className="md:col-span-5 flex justify-end">
          <div
            className="hero-glass-card animate-shimmer-card w-full max-w-sm p-6 md:p-8"
            style={{ animationDelay: "2.5s" }}>
            
            <div className="flex items-start gap-4 mb-6">
              <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-gold/40 flex-shrink-0">
                <AppImage
                  src="/assets/images/R-1771586385297.jpg"
                  alt="Portrait representing Dr. Walter T. Gwenigale, Liberian physician and public servant"
                  width={56}
                  height={56}
                  className="w-full h-full object-cover" />
                
              </div>
              <div>
                <p className="font-heading text-white font-semibold leading-tight text-base">
                  Dr. Walter T. Gwenigale
                </p>
                <p className="text-white/55 text-xs font-body mt-1 leading-snug">
                  Former Minister of Health,
                  <br />
                  Republic of Liberia
                </p>
              </div>
            </div>

            <blockquote className="text-white/80 font-heading italic text-sm leading-relaxed border-l-2 border-gold/50 pl-4 mb-6">
              "Healthcare is a right, not a privilege. Every Liberian deserves
              access to quality medical care."
            </blockquote>

            <div className="grid grid-cols-2 gap-4 border-t border-white/10 pt-5">
              <div>
                <p className="text-gold text-2xl font-heading font-bold">50+</p>
                <p className="text-white/50 text-xs font-body mt-0.5">
                  Years as Surgeon
                </p>
              </div>
              <div>
                <p className="text-gold text-2xl font-heading font-bold">9</p>
                <p className="text-white/50 text-xs font-body mt-0.5">
                  Years as Health Minister
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 scroll-indicator">
        <span className="text-white/40 text-[10px] uppercase tracking-widest font-body">
          Scroll
        </span>
        <div className="scroll-indicator-line mt-2" />
      </div>
    </section>);

}